<template>
  <div class="width-100 h-100">
    <ReportInputItem :title="title" data-id-e2e="checkpointsSelectionTable">
      <div v-if="clusteredToggle" class="d-flex align-left justify-left checkbox-container mb-1">
        <div class="checkbox-label">
          {{ tkey('useUnclustered') }}
        </div>
        <v-checkbox
          class="rtls-checkbox mx-2"
          :model-value="useUnclustered"
          @update:model-value="useUnclustered = $event"
        />
      </div>

      <!-- Main table with canvases. :items-per-page="-1" is a workaround for Vuetify table to avoid pagination.
      Needs to be used together with disable-sort, disable-pagination and hide-default-footer -->
      <v-data-table
        class="striped-table simple-assortments-table simple-assortments-table--extracts"
        :headers="headers"
        :items="filteredCanvases"
        :items-per-page="-1"
        max-height="maxHeight"
        density="compact"
        fixed-header
        disable-sort
        disable-pagination
        hide-default-footer
      >
        <template #item.status="{ item }">
          <v-icon size="18">
            {{ `$${item.status}` }}
          </v-icon>
        </template>

        <!-- Checkpoint selection -->
        <template #item.checkpoint="{ item }">
          <div class="d-flex">
            <v-autocomplete
              ref="autocomplete"
              v-model="selectedCheckpoints[item._id]"
              v-model:search="searchText[item._id]"
              data-id-e2e="checkpointsSelectionAutocomplete"
              :items="filteredCheckpointsByCanvasId[item._id]"
              :custom-filter="filterCheckpoints"
              :placeholder="tkey('placeholders.choose')"
              :menu-props="{ auto: true }"
              :rules="rules"
              bg-color="white"
              width="240px"
              item-title="text"
              density="compact"
              hide-messages
              hide-details
              @update:model-value="updateSelectedCheckpoints()"
            />
            <ErrorTriangle
              class="ml-2 mr-2"
              :style="{ visibility: isCheckpointError ? 'visible' : 'hidden' }"
              :errors="{
                [tkey('checkpointErrorMsg')]: !isCheckpointError,
              }"
            />
            <!-- View icon -->
            <v-icon
              v-if="canViewCheckpointData"
              color="primary"
              class="material-icons icon mr-3"
              :disabled="!selectedCheckpoints[item._id]"
              @click="viewCheckpointData(item._id)"
            >
              mdi-eye
            </v-icon>
          </div>
        </template>
      </v-data-table>
    </ReportInputItem>
  </div>
</template>

<script>
import { filter, get, isEmpty, map } from 'lodash';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';

export default {
  props: {
    scenarioId: {
      type: String,
      required: false,
      default: () => null,
    },
    headers: {
      type: Array,
      required: true,
    },
    canvases: {
      type: Array,
      required: true,
    },
    checkpointsByCanvasId: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: () => '',
    },
    maxHeight: {
      type: String,
      required: false,
      default: () => '500px',
    },
    shouldValidateCheckpoints: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    canViewCheckpointData: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    clusteredToggle: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },

  setup() {
    const { tkey } = useInternationalization('checkpointSelectionTable');
    const { canEditProductStoreExtracts } = usePermission();

    return { tkey, canEditProductStoreExtracts };
  },

  data() {
    return {
      selectedCheckpoints: {},
      searchText: {},
      rules: this.shouldValidateCheckpoints ? [value => this.validateCheckpoints(value)] : [],
      isCheckpointError: false,
      useUnclustered: false,
    };
  },

  computed: {
    isSelectCheckpointDisabled() {
      return !this.canEditProductStoreExtracts;
    },
    filteredCanvases() {
      return this.clusteredToggle
        ? filter(
            this.canvases,
            c => (this.useUnclustered && !c.clusterName) || (!this.useUnclustered && c.clusterName)
          )
        : this.canvases;
    },
    filteredCheckpointsByCanvasId() {
      const filteredCanvasIds = map(this.filteredCanvases, c => c._id);
      const checkpointsByCanvasIdKeys = Object.keys(this.checkpointsByCanvasId);
      const filteredKeys = filter(checkpointsByCanvasIdKeys, k => filteredCanvasIds.includes(k));
      const result = {};

      filteredKeys.forEach(key => {
        result[key] = this.checkpointsByCanvasId[key];
      });
      return result;
    },
  },

  watch: {
    canvases() {
      this.resetData();
    },
    useUnclustered() {
      this.resetData();
    },
  },

  async created() {
    await this.resetData();
  },

  methods: {
    filterCheckpoints(item, queryText) {
      if (isEmpty(queryText)) return true;

      const itemName = typeof item === 'object' ? get(item, 'text', '') : item;
      const query = queryText.toLocaleLowerCase();
      return itemName.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) > -1;
    },

    validateCheckpoints(value) {
      this.isCheckpointError = Object.values(this.selectedCheckpoints).every(v => !v);
      return !!value;
    },

    updateSelectedCheckpoints() {
      this.$emit('update-selected-checkpoints', this.selectedCheckpoints);
    },

    viewCheckpointData(canvasId) {
      const checkpointId = this.selectedCheckpoints[canvasId];
      const checkpointOptions = this.filteredCheckpointsByCanvasId[canvasId] || [];
      const name = (checkpointOptions.find(opt => opt.value === checkpointId) || {}).text;
      this.$emit('view-checkpoint-data', { checkpointId, name });
    },

    async resetData() {
      this.searchText = {};
      if (this.shouldValidateCheckpoints) this.validateCheckpoints();

      // Pre-fill initial selected checkpoints map with ids
      this.selectedCheckpoints = {};
      Object.keys(this.filteredCheckpointsByCanvasId).forEach(k => {
        this.selectedCheckpoints[k] = null;
      });

      this.updateSelectedCheckpoints();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.v-input__control) {
  .v-select__slot {
    margin-left: 5px;
  }
  // hide messages box on autocomplete
  .v-text-field__details {
    display: none;
  }
}

:deep(.v-data-table__wrapper) {
  overflow-y: hidden;
}

:deep(.checkbox-container) {
  border-radius: 4px;
  padding: 2px 1px 2px 10px;
}

:deep(.checkbox-label) {
  font-size: 1.3rem;
  color: $assortment-font-colour;
  padding-left: 10px;
}
</style>
